// Port of StreakService.getStreakSnapshot (lib/services/streak_service.dart).
// Pure functions over already-loaded data so pages can recompute live.
import { DAY_KEYS } from '../i18n';
import { addDays, isoWeekday, isSameDay, parseDateKey, startOfDay, weekDates } from './dates';
import { dayHasWorkout } from './program';

export const FREEZE_MONTHLY_CAP = 2;

const has = (set, d) => set.has(+startOfDay(d));

function completedWorkoutSet(progressDays) {
  const s = new Set();
  progressDays.forEach((p) => {
    if (!p.completed) return;
    const d = parseDateKey(p.id);
    if (d) s.add(+d);
  });
  return s;
}

function nutritionLoggedSet(nutritionDays) {
  const s = new Set();
  nutritionDays.forEach((n) => {
    if (!(n.entries || []).length) return;
    const d = parseDateKey(n.date || n.id);
    if (d) s.add(+d);
  });
  return s;
}

export function frozenSet(userData, type) {
  const s = new Set();
  Object.keys(userData?.streakFreezes?.[type] || {}).forEach((k) => {
    const d = parseDateKey(k);
    if (d) s.add(+d);
  });
  return s;
}

export function programStreak(today, completed, program, frozen) {
  if (!program || completed.size === 0) return 0;
  const earliest = Math.min(...completed);
  const t = startOfDay(today);
  const todayHas = dayHasWorkout(program, isoWeekday(t) - 1);
  let day = todayHas && has(completed, t) ? t : addDays(t, -1);
  if (+day < earliest) return 0;
  let streak = 0;
  while (+day >= earliest) {
    if (dayHasWorkout(program, isoWeekday(day) - 1)) {
      if (has(completed, day) || has(frozen, day)) streak += 1;
      else break;
    }
    day = addDays(day, -1);
  }
  return streak;
}

export function nutritionStreak(today, logged, frozen) {
  if (logged.size === 0) return 0;
  const earliest = Math.min(...logged);
  const t = startOfDay(today);
  let day = has(logged, t) ? t : addDays(t, -1);
  if (+day < earliest) return 0;
  let streak = 0;
  while (+day >= earliest) {
    if (has(logged, day) || has(frozen, day)) streak += 1;
    else break;
    day = addDays(day, -1);
  }
  return streak;
}

const usedThisMonth = (frozen, ref) => [...frozen].filter((ms) => {
  const d = new Date(ms);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
}).length;

export function computeStreakSnapshot({ progressDays = [], program = null, nutritionDays = [], userData = null, today = new Date() }) {
  const completed = completedWorkoutSet(progressDays);
  const logged = nutritionLoggedSet(nutritionDays);
  const frozenProgram = frozenSet(userData, 'program');
  const frozenNutrition = frozenSet(userData, 'nutrition');

  const week = weekDates(today).map((date, i) => {
    const hasWorkout = dayHasWorkout(program, i);
    const workoutDone = hasWorkout && has(completed, date);
    const nutritionLogged = has(logged, date);
    return {
      dayKey: DAY_KEYS[i],
      date,
      hasWorkout,
      workoutDone,
      nutritionLogged,
      workoutFrozen: hasWorkout && !workoutDone && has(frozenProgram, date),
      nutritionFrozen: !nutritionLogged && has(frozenNutrition, date),
      isToday: isSameDay(date, today),
    };
  });

  const programDays = programStreak(today, completed, program, frozenProgram);
  const nutritionDaysCount = nutritionStreak(today, logged, frozenNutrition);

  const yesterday = addDays(startOfDay(today), -1);
  const programYesterdayMissing = !!program && dayHasWorkout(program, isoWeekday(yesterday) - 1)
    && !has(completed, yesterday) && !has(frozenProgram, yesterday);
  const programRecoverable = programYesterdayMissing
    ? programStreak(today, completed, program, new Set([...frozenProgram, +yesterday])) : 0;
  const programFreezesRemaining = Math.max(0, FREEZE_MONTHLY_CAP - usedThisMonth(frozenProgram, today));

  const nutritionYesterdayMissing = !has(logged, yesterday) && !has(frozenNutrition, yesterday);
  const nutritionRecoverable = nutritionYesterdayMissing
    ? nutritionStreak(today, logged, new Set([...frozenNutrition, +yesterday])) : 0;
  const nutritionFreezesRemaining = Math.max(0, FREEZE_MONTHLY_CAP - usedThisMonth(frozenNutrition, today));

  return {
    week,
    programStreakDays: programDays,
    nutritionStreakDays: nutritionDaysCount,
    programFreezeAvailable: programYesterdayMissing && programRecoverable > 0 && programFreezesRemaining > 0,
    programFreezeRecoverableDays: programRecoverable,
    programFreezesRemaining,
    nutritionFreezeAvailable: nutritionYesterdayMissing && nutritionRecoverable > 0 && nutritionFreezesRemaining > 0,
    nutritionFreezeRecoverableDays: nutritionRecoverable,
    nutritionFreezesRemaining,
    todayHasWorkout: week.some((d) => d.isToday && d.hasWorkout),
    todayWorkoutDone: week.some((d) => d.isToday && d.workoutDone),
    todayNutritionLogged: week.some((d) => d.isToday && d.nutritionLogged),
  };
}

// ── Badge metrics over day numbers (BadgeService helpers) ──
// UTC day numbers so a DST shift can't break two consecutive calendar days apart.
const dayNo = (ms) => {
  const d = new Date(ms);
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000;
};
const dayNos = (msSet) => new Set([...msSet].map(dayNo));
/** year * 12 + month0: a sortable, gap-free month index. */
const monthOf = (n) => {
  const d = new Date(n * 86400000);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
};
const sortedDays = (set) => [...set].sort((a, b) => a - b);

/** Completed 7-day blocks across every run of consecutive days — 7-day run = 1, 15-day run = 2. */
export function weekBlocks(days) {
  const sorted = sortedDays(days);
  let weeks = 0;
  let run = 0;
  sorted.forEach((day, i) => {
    run = i > 0 && day === sorted[i - 1] + 1 ? run + 1 : 1;
    if (run % 7 === 0) weeks += 1;
  });
  return weeks;
}

/** Run of consecutive days ending today — or yesterday while today isn't done yet. */
function currentRun(days, today) {
  let day = dayNo(+startOfDay(today));
  if (!days.has(day)) day -= 1;
  let run = 0;
  while (days.has(day)) {
    run += 1;
    day -= 1;
  }
  return run;
}

/** Logged (or frozen, as in the live streak) nutrition days, as 7-day blocks. */
export function nutritionStreakWeeks(nutritionDays, userData) {
  return weekBlocks(dayNos([...nutritionLoggedSet(nutritionDays), ...frozenSet(userData, 'nutrition')]));
}

function waterGoalDays(waterDays, goalMl) {
  const s = new Set();
  waterDays.forEach((w) => {
    const d = parseDateKey(w.date || w.id);
    if (d && (Number(w.ml) || 0) >= goalMl) s.add(dayNo(+d));
  });
  return s;
}

export const waterGoalStreak = (waterDays, goalMl, today = new Date()) => currentRun(waterGoalDays(waterDays, goalMl), today);
export const waterGoalWeeks = (waterDays, goalMl) => weekBlocks(waterGoalDays(waterDays, goalMl));

/** Calendar months with at least one body measurement. */
export function measuredMonthCount(bodyDays) {
  const months = new Set();
  bodyDays.forEach((b) => {
    const d = parseDateKey(b.id);
    if (d) months.add(monthOf(dayNo(+d)));
  });
  return months.size;
}

/**
 * Months containing a comeback: 3 active days (workout done, nutrition logged or frozen)
 * in a row right after 3+ inactive days. The first-ever run doesn't count, and one per
 * month keeps a deliberate lapse from paying off repeatedly.
 */
export function comebackCount(progressDays, nutritionDays, userData) {
  const active = dayNos([
    ...completedWorkoutSet(progressDays),
    ...nutritionLoggedSet(nutritionDays),
    ...frozenSet(userData, 'nutrition'),
    ...frozenSet(userData, 'program'),
  ]);
  const months = new Set();
  let runStart = null;
  let endBeforeRun = null;
  sortedDays(active).forEach((day, i, all) => {
    if (i === 0 || day !== all[i - 1] + 1) {
      endBeforeRun = i === 0 ? null : all[i - 1];
      runStart = day;
    }
    if (day - runStart === 2 && endBeforeRun != null && runStart - endBeforeRun - 1 >= 3) months.add(monthOf(day));
  });
  return months.size;
}

/** Calendar months where every scheduled workout day was completed; the current month counts once its last one is done. */
export function perfectMonthCount(progressDays, program, today = new Date()) {
  if (!program) return 0;
  const completed = completedWorkoutSet(progressDays);
  if (!completed.size) return 0;
  const earliest = new Date(Math.min(...completed));
  let month = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
  const current = new Date(today.getFullYear(), today.getMonth(), 1);
  let perfect = 0;
  while (+month <= +current) {
    let scheduled = 0;
    let done = 0;
    for (let day = month; day.getMonth() === month.getMonth(); day = addDays(day, 1)) {
      if (dayHasWorkout(program, isoWeekday(day) - 1)) {
        scheduled += 1;
        if (has(completed, day)) done += 1;
      }
    }
    if (scheduled > 0 && done === scheduled) perfect += 1;
    month = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  }
  return perfect;
}

/** Weeks (Mon–Sun) where every scheduled workout day was completed (BadgeService._computePerfectWeekCount). */
export function perfectWeekCount(progressDays, program, today = new Date()) {
  if (!program) return 0;
  const completed = completedWorkoutSet(progressDays);
  if (!completed.size) return 0;
  const earliest = new Date(Math.min(...completed));
  let weekStart = addDays(earliest, -(isoWeekday(earliest) - 1));
  const t = startOfDay(today);
  const current = addDays(t, -(isoWeekday(t) - 1));
  let perfect = 0;
  while (+weekStart <= +current) {
    let scheduled = 0;
    let done = 0;
    for (let i = 0; i < 7; i += 1) {
      const day = addDays(weekStart, i);
      if (dayHasWorkout(program, isoWeekday(day) - 1)) {
        scheduled += 1;
        if (has(completed, day)) done += 1;
      }
    }
    if (scheduled > 0 && done === scheduled) perfect += 1;
    weekStart = addDays(weekStart, 7);
  }
  return perfect;
}
